/**
 * External dependencies
 */
import { first, last } from 'lodash';
/**
 * WordPress dependencies
 */

import { useEffect } from '@wordpress/element';
import { focus, isTextField, placeCaretAtHorizontalEdge } from '@wordpress/dom';
import { useSelect } from '@wordpress/data';
/**
 * Internal dependencies
 */

import { isInsideRootBlock } from '../../../utils/dom';
/** @typedef {import('@wordpress/element').RefObject} RefObject */

/**
 * Returns the initial position if the block needs to be focussed, `undefined`
 * otherwise. The initial position is either 0 (start) or -1 (end).
 *
 * @param {string} clientId Block client ID.
 *
 * @return {number} The initial position, either 0 (start) or -1 (end).
 */

function useInitialPosition(clientId) {
  return useSelect(function (select) {
    var _select = select('core/block-editor'),
        getSelectedBlocksInitialCaretPosition = _select.getSelectedBlocksInitialCaretPosition,
        isMultiSelecting = _select.isMultiSelecting,
        isNavigationMode = _select.isNavigationMode,
        isBlockSelected = _select.isBlockSelected;

    if (!isBlockSelected(clientId)) {
      return;
    }

    if (isMultiSelecting() || isNavigationMode()) {
      return;
    } // If there's no initial position, return 0 to focus the start.


    return getSelectedBlocksInitialCaretPosition() || 0;
  }, [clientId]);
}
/**
 * Transitions focus to the block or inner tabbable when the block becomes
 * selected.
 *
 * @param {RefObject} ref      React ref with the block element.
 * @param {string}    clientId Block client ID.
 */


export function useFocusFirstElement(ref, clientId) {
  var initialPosition = useInitialPosition(clientId);
  useEffect(function () {
    if (initialPosition === undefined) {
      return;
    }

    var ownerDocument = ref.current.ownerDocument; // Focus is captured by the wrapper node, so while focus transition
    // should only consider tabbables within editable display, since it
    // may be the wrapper itself or a side control which triggered the
    // focus event, don't unnecessary transition to an inner tabbable.

    if (ownerDocument.activeElement && isInsideRootBlock(ref.current, ownerDocument.activeElement)) {
      return;
    } // Find all tabbables within node.


    var textInputs = focus.tabbable.find(ref.current).filter(function (node) {
      return isTextField(node) && // Exclude inner blocks and block appenders
      isInsideRootBlock(ref.current, node) && !node.closest('.block-list-appender');
    }); // If reversed (e.g. merge via backspace), use the last in the set of
    // tabbables.

    var isReverse = -1 === initialPosition;
    var target = (isReverse ? last : first)(textInputs) || ref.current;
    placeCaretAtHorizontalEdge(target, isReverse);
  }, [initialPosition]);
}
//# sourceMappingURL=use-focus-first-element.js.map